/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useCallback } from 'react';
import useSWRInfinite from 'swr/infinite';
import fetch from '@api/fetchers';
import { HOST_TYPE } from '@utils/api-helper/host';
import getPaginationResults from '@utils/swrHelper';

const defaultSwrOptions = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  dedupingInterval: 15000
};

const useInfiniteFetch = (key, fetcherOptions, swrOptions = null) => {
  const {
    useAuthToken = true,
    useSearchToken = false,
    isFormData = false,
    hostType = HOST_TYPE.DEFAULT,
    hasAppClient = false,
    params = {},
    pathVars = {},
    limit = 10,
    useCamelCase = true,
    applyOffsetsAutomatically = true,
    hasQueryParams = false,
    resolveNextPageKey = false
  } = fetcherOptions || {};

  const options = useMemo(() => {
    return {
      useAuthToken,
      useSearchToken,
      isFormData,
      host: hostType,
      hasAppClient,
      params,
      pathVars,
      useCamelCase
    };
  }, [
    useAuthToken,
    useSearchToken,
    isFormData,
    useCamelCase,
    hostType,
    hasAppClient,
    params,
    pathVars
  ]);

  const fetcher = useCallback(
    (...args: any[]) => fetch(...args, options),
    [key, options]
  );

  const { data, error, size, setSize, ...rest } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (!key) return null;
      const limitOffsetParams = hasQueryParams
        ? `&offset=${pageIndex * limit}&limit=${limit}`
        : `?offset=${pageIndex * limit}&limit=${limit}`;

      if (!resolveNextPageKey) {
        return (
          previousPageData?.next ||
          `${key}${applyOffsetsAutomatically ? limitOffsetParams : ''}`
        );
      }

      if (pageIndex === 0) {
        return `${key}${applyOffsetsAutomatically ? limitOffsetParams : ''}`;
      }

      const next = previousPageData?.next;

      if (next) {
        return resolveNextPageKey(next, key);
      }

      if (applyOffsetsAutomatically) {
        return `${key}${limitOffsetParams}`;
      }

      return null;
    },
    fetcher,
    { ...defaultSwrOptions, ...(swrOptions || {}) }
  );

  return {
    key,
    rawData: data,
    data:
      data && data.length > 0 && !error
        ? getPaginationResults(data)
        : swrOptions?.fallbackData || { count: 0, results: [] },
    size,
    setSize,
    isLoading: !error && !data,
    isError: error,
    ...rest // rest -> isValidating, mutate
  };
};

export default useInfiniteFetch;
